import { useSyncExternalStore } from 'react';

import { createSessionStore } from '@spotify/confidence-flag-evaluation-react';

import {
  EMPTY_IMPERSONATION,
  IMPERSONATION_KEY,
  type ImpersonationContext,
  parseImpersonationContext,
} from './impersonationContext';

const impersonationStore = createSessionStore<ImpersonationContext>({
  key: IMPERSONATION_KEY,
  empty: EMPTY_IMPERSONATION,
  parse: parseImpersonationContext,
  serialize: context => JSON.stringify(context),
});

const everImpersonatedKeys = new Set<string>();
let recording = false;

const recordKeys = (): void => {
  for (const key of Object.keys(impersonationStore.read())) {
    everImpersonatedKeys.add(key);
  }
};

const ensureRecording = (): void => {
  if (recording) {
    return;
  }
  recording = true;
  impersonationStore.subscribe(recordKeys);
  recordKeys();
};

export const readImpersonationContext = (): ImpersonationContext =>
  impersonationStore.read();

export const subscribeToImpersonation = (
  listener: () => void,
): (() => void) => {
  ensureRecording();
  return impersonationStore.subscribe(listener);
};

export const useImpersonationContext = (): ImpersonationContext =>
  useSyncExternalStore(
    impersonationStore.subscribe,
    impersonationStore.getSnapshot,
    impersonationStore.getServerSnapshot,
  );

export const useIsImpersonating = (): boolean =>
  Object.keys(useImpersonationContext()).length > 0;

export const readEverImpersonatedKeys = (): ReadonlySet<string> => {
  ensureRecording();
  return everImpersonatedKeys;
};

export const setImpersonationContext = (next: ImpersonationContext): void =>
  impersonationStore.write(next);
